import { set } from 'lodash';
import { t } from 'i18next';
import { DashStyleValue, OptionsPanKeyValue, SeriesOptionsType } from 'highcharts';
import { ArrowKey, GraphFlag, GraphPoint, GraphPointMax, GraphPointRR, Point } from 'models/measurement';
import arrows from 'assets/images/arrows';

function addTooltip(x = true, max = false) {
  const pointX = x ? '{point.x}' : '{point.t}';
  const pointY = max ? '{point.z}' : '{point.y}';
  const format = `<span style="color:{point.color}">\u25CF</span><span style="font-size:9px"> (${pointX},${pointY})</span>`;

  return { headerFormat: '', pointFormat: format, useHTML: true };
}

export function getGraphHeight(windowHeight: number) {
  return windowHeight > 900 ? 350 : 250;
}

const getPeaksRadius = (windowHeight: number) => {
  return windowHeight > 900 ? 3.8 : 2;
};

export function PpgChartOptions(
  ppgData: GraphPoint[],
  peaksData: GraphPoint[],
  flagsData: GraphFlag[],
  windowHeight: number,
) {
  return {
    title: {
      text: '',
    },
    legend: { enabled: false },
    credits: { enabled: false },
    xAxis: {
      crosshair: { snap: true },
      visible: true,
      min: 0,
      max: 60,
      lineColor: 'var(--mantine-color-gray-5)',
      tickColor: 'var(--mantine-color-gray-5)',
      labels: {
        format: '{value}s',
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '9px',
          fontWeight: '700',
        },
      },
      plotBands: flagsData,
    },
    yAxis: {
      crosshair: { snap: true },
      title: { text: '' },
      visible: true,
      labels: {
        format: '{value:.4f}',
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '9px',
          fontWeight: '700',
        },
      },
    },
    plotOptions: {
      line: {
        animation: false,
      },
      series: {
        animation: false,
        enableMouseTracking: true,
        allowPointSelect: true,
        marker: {
          states: {
            select: {
              fillColor: 'var(--mantine-color-red-5)',
            },
          },
        },

        zIndex: 5,
      },
    },
    tooltip: {
      enabled: false,
    },
    chart: {
      zoomType: 'xy',
      type: 'line',
      panning: { enabled: true },
      panKey: 'shift' as OptionsPanKeyValue,
      height: getGraphHeight(windowHeight),
      animation: false,
      resetZoomButton: {
        theme: {
          fill: 'white',
          stroke: 'silver',
          r: 0,
          zIndex: 100,
          states: {
            hover: {
              fill: 'var(--mantine-color-turquoise-5)',
              style: {
                color: 'white',
                borderRadius: '3px',
              },
            },
          },
        },
      },
      accessibility: {
        enabled: false,
      },
    },
    series: [
      {
        data: ppgData,
        lineWidth: 1,
        cursor: 'crosshair',
        states: {
          select: {
            radius: 2,
            fillColor: 'var(--mantine-color-red-5)',
          },
          hover: {
            enabled: false,
          },
        },
        softThreshold: true,
        name: false,
        allowPointSelect: true,
        color: 'white',
        fillOpacity: 0.1,
        animation: false,
        lineColor: 'var(--mantine-color-turquoise-5)',
        marker: {
          enabled: false,
        },
      },
      {
        data: peaksData,
        type: 'scatter',
        cursor: 'pointer',
        marker: {
          animation: true,
          lineColor: 'var(--mantine-color-turquoise-5)',
          symbol: 'circle',
          radius: getPeaksRadius(windowHeight),
          fillColor: 'var(--mantine-color-red-5)',
          lineWidth: 1,
        },
      },
    ] as SeriesOptionsType[],
    accessibility: {
      enabled: false,
    },
  };
}

const ATRIAL_RATE_BPM = 300;
const AV_CONDUCTION_RATIOS = [2, 3, 4, 5];
const AV_CONDUCTION_BANDS_BPM = AV_CONDUCTION_RATIOS.map((ratio) => ATRIAL_RATE_BPM / ratio); // 150, 100, 75, 60

const LOW_RATE_THRESHOLD_BPM = 40;
const HIGH_RATE_THRESHOLD_BPM = 110;
const LOW_RATE_THRESHOLD_MS = Math.round(60000 / LOW_RATE_THRESHOLD_BPM);
const HIGH_RATE_THRESHOLD_MS = Math.round(60000 / HIGH_RATE_THRESHOLD_BPM);

export function RrChartOptions(rrData: GraphPointRR[], chartHeight: number) {
  const max = 200;
  const points: Point[] = [];
  const badPoints: Point[] = [];
  const yMax: Point[] = [];
  const yMaxBad: Point[] = [];

  rrData.forEach((val: GraphPointRR, index: number) => {
    const rrMs = Math.round(val[0]);
    const badFlag = val[1];
    const bpm = Math.round(60000 / rrMs);

    if (bpm < max) {
      if (badFlag) {
        badPoints.push({ x: index, y: bpm, t: rrMs });
      } else {
        points.push({ x: index, y: bpm, t: rrMs });
      }
    } else if (badFlag) {
      yMaxBad.push({ x: index, y: max, z: bpm, t: rrMs });
    } else {
      yMax.push({ x: index, y: max, z: bpm, t: rrMs });
    }
  });

  const series = [
    { data: points, color: 'var(--mantine-color-turquoise-5)', tooltip: addTooltip(false), type: 'scatter' },
    { data: badPoints, color: 'var(--mantine-color-gray-5)', tooltip: addTooltip(false), type: 'scatter' },
    {
      data: yMax,
      color: 'var(--mantine-color-turquoise-5)',
      marker: { symbol: arrows.top.single.good },
      tooltip: addTooltip(false, true),
      type: 'scatter',
    },
    {
      data: yMaxBad,
      color: 'var(--mantine-color-gray-5)',
      marker: { symbol: arrows.top.single.bad },
      tooltip: addTooltip(false, true),
      type: 'scatter',
    },
  ] as SeriesOptionsType[];

  return {
    series,
    credits: { enabled: false },
    title: {
      text: '',
    },
    legend: { enabled: false },
    xAxis: {
      title: {
        text: t('patients_tachogram_amount'),
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '10px',
        },
      },
      crosshair: {
        snap: false,
      },
      min: 0,
      visible: true,
      lineColor: 'var(--mantine-color-gray-5)',
      tickColor: 'var(--mantine-color-gray-5)',
      labels: {
        format: '{value}',
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '9px',
        },
      },
    },
    yAxis: {
      title: {
        text: t('patients_tachogram_rate'),
        margin: 10,
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '10px',
        },
      },
      min: 0,
      max,
      crosshair: true,
      visible: true,
      gridLineWidth: 0,
      labels: {
        format: '{value}',
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '9px',
        },
      },
      plotBands: [
        { from: HIGH_RATE_THRESHOLD_BPM, to: max, color: 'var(--mantine-color-heartRateOutOfRange-5)' },
        { from: 0, to: LOW_RATE_THRESHOLD_BPM, color: 'var(--mantine-color-heartRateOutOfRange-5)' },
      ],
      plotLines: [
        {
          value: HIGH_RATE_THRESHOLD_BPM,
          color: 'var(--mantine-color-red-2)',
          dashStyle: 'Dash' as DashStyleValue,
          width: 1,
        },
        {
          value: LOW_RATE_THRESHOLD_BPM,
          color: 'var(--mantine-color-red-2)',
          dashStyle: 'Dash' as DashStyleValue,
          width: 1,
        },
        ...AV_CONDUCTION_BANDS_BPM.map((value) => ({
          value,
          color: 'var(--mantine-color-conductionBlue-5)',
          width: 1,
        })),
      ],
    },
    chart: {
      zoomType: false,
      type: 'scatter',
      panning: { enabled: false },
      panKey: 'shift' as OptionsPanKeyValue,
      height: chartHeight,
      animation: false,
    },
    plotOptions: {
      series: {
        softThreshold: false,
        allowPointSelect: true,
        fillOpacity: 0,
        animation: false,
        lineWidth: 0,
        cursor: 'pointer',
        marker: {
          radius: 3,
          symbol: 'circle',
        },
        zIndex: 5,
      },
    },
    accessibility: {
      enabled: false,
    },
  };
}

export function PcChartOptions(pcData: GraphPointRR[], size: number, showAllDatapoints: boolean) {
  const max = 2000;
  const points: GraphPoint[] = [];
  const badPoints: GraphPoint[] = [];
  const xMax: GraphPointMax[] = [];
  const yMax: GraphPointMax[] = [];
  const xyMax: GraphPointMax[] = [];

  const offs = pcData.slice(0, -1);

  function addTooltipExtraPoint(x: number, y: number, badFlag: boolean) {
    const colorPoint = badFlag ? 'var(--mantine-color-gray-5)' : 'var(--mantine-color-turquoise-5)';
    return `<span style="color:${colorPoint}">\u25CF</span><span style="font-size:9px"> (${x},${y})</span> `;
  }

  function addTooltipHtml(html: string) {
    return { headerFormat: '', pointFormat: html, useHTML: true };
  }

  function groupPoint(values: GraphPointMax, point: number, offset: number, badFlag: boolean): GraphPointMax {
    const tooltip = `${values.tooltip}<br>${addTooltipExtraPoint(point, offset, badFlag)}`;
    const newMarker = badFlag ? 'bad' : 'good';
    const marker = values.marker !== newMarker ? 'both' : newMarker;

    return { ...values, tooltip, arrow: 'double', marker };
  }

  function setOutput(value: GraphPointMax, arrowKey: ArrowKey) {
    return {
      data: [[value.x, value.y] as GraphPoint],
      color: value.marker === 'good' ? 'var(--mantine-color-turquoise-5)' : 'var(--mantine-color-gray-5)',
      marker: { symbol: arrows[arrowKey][value.arrow][value.marker] },
      tooltip: addTooltipHtml(value.tooltip),
      type: 'scatter',
    } as SeriesOptionsType;
  }

  pcData.slice(1).forEach((val: GraphPointRR, index: number) => {
    const offval = offs[index];
    const point = Math.round(val[0]);
    const offset = Math.round(offval[0]);
    const badFlag = !!(val[1] || offval[1]);

    if (badFlag && !showAllDatapoints) return;

    // points out of bounds
    if (point > max && offset > max) {
      // out of both border
      if (xyMax.length > 0) {
        // add to list
        set(xyMax, 0, groupPoint(xyMax[0], point, offset, badFlag));
      } else {
        xyMax.push({
          x: max,
          y: max,
          tooltip: addTooltipExtraPoint(point, offset, badFlag),
          arrow: 'single',
          marker: badFlag ? 'bad' : 'good',
        });
      }
    } else if (point > max) {
      // out of x border
      let isNew = true;
      xMax.forEach((value, key) => {
        // group same values
        if (value.y === offset) {
          // check if already in the list
          set(xMax, key, groupPoint(value, point, offset, badFlag));
          isNew = false;
        }
      });
      if (isNew) {
        // add new value
        xMax.push({
          x: max,
          y: offset,
          tooltip: addTooltipExtraPoint(point, offset, badFlag),
          arrow: 'single',
          marker: badFlag ? 'bad' : 'good',
        });
      }
    } else if (offset > max) {
      // out of y border
      let isNew = true;
      yMax.forEach((value, key) => {
        // group same values
        if (value.x === point) {
          // check if already in the list
          set(yMax, key, groupPoint(value, point, offset, badFlag));
          isNew = false;
        }
      });
      if (isNew) {
        // add new value
        yMax.push({
          x: point,
          y: max,
          tooltip: addTooltipExtraPoint(point, offset, badFlag),
          arrow: 'single',
          marker: badFlag ? 'bad' : 'good',
        });
      }
    } else if (badFlag) {
      badPoints.push([point, offset]);
    } else {
      points.push([point, offset]);
    }
  });

  const series = [
    {
      data: [
        [0, 0],
        [max, max],
      ],
      type: 'line',
      color: 'var(--mantine-color-gray-5)',
      dashStyle: 'Dash',
      lineWidth: 1,
      marker: { enabled: false },
      enableMouseTracking: false,
      zIndex: 0,
    },
    { data: badPoints, color: 'var(--mantine-color-gray-5)', tooltip: addTooltip(), type: 'scatter', zIndex: 1 },
    { data: points, color: 'var(--mantine-color-turquoise-5)', tooltip: addTooltip(), type: 'scatter', zIndex: 2 },
  ] as SeriesOptionsType[];

  xMax.forEach((value: GraphPointMax) => {
    series.push(setOutput(value, 'right') as SeriesOptionsType);
  });
  yMax.forEach((value: GraphPointMax) => {
    series.push(setOutput(value, 'top'));
  });
  xyMax.forEach((value: GraphPointMax) => {
    series.push(setOutput(value, 'corner'));
  });

  return {
    series,
    credits: { enabled: false },
    title: {
      text: '',
    },
    legend: { enabled: false },
    xAxis: {
      title: {
        text: t('patients_lorenz_interval'),
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '10px',
        },
      },
      crosshair: {
        snap: false,
      },
      min: 0,
      max: 2000,
      tickInterval: 500,
      pointStart: 0,
      lineColor: 'var(--mantine-color-gray-5)',
      tickColor: 'var(--mantine-color-gray-5)',
      labels: {
        format: '{value}',
        step: 1,
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '9px',
        },
      },
      plotLines: [
        {
          value: HIGH_RATE_THRESHOLD_MS,
          color: 'var(--mantine-color-red-2)',
          dashStyle: 'Dash' as DashStyleValue,
          width: 1,
        },
        {
          value: LOW_RATE_THRESHOLD_MS,
          color: 'var(--mantine-color-red-2)',
          dashStyle: 'Dash' as DashStyleValue,
          width: 1,
        },
      ],
    },
    yAxis: {
      title: {
        text: t('patients_lorenz_previous_interval'),
        margin: 10,
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '10px',
        },
      },
      crosshair: true,
      min: 0,
      max: 2000,
      tickInterval: 500,
      gridLineWidth: 0,
      labels: {
        format: '{value}',
        style: {
          color: 'var(--mantine-color-gray-5)',
          fontSize: '9px',
        },
      },
      plotLines: [
        {
          value: HIGH_RATE_THRESHOLD_MS,
          color: 'var(--mantine-color-red-2)',
          dashStyle: 'Dash' as DashStyleValue,
          width: 1,
        },
        {
          value: LOW_RATE_THRESHOLD_MS,
          color: 'var(--mantine-color-red-2)',
          dashStyle: 'Dash' as DashStyleValue,
          width: 1,
        },
      ],
    },
    chart: {
      zoomType: false,
      type: 'scatter',
      panning: { enabled: false },
      panKey: 'shift' as OptionsPanKeyValue,
      height: size,
      width: size,
      spacingLeft: 5,
      spacingRight: 20,
      animation: false,
      align: 'center',
    },
    plotOptions: {
      line: {
        animation: false,
      },
      series: {
        animation: false,
        enableMouseTracking: true,
        allowPointSelect: true,
        stickyTracking: false,
        softThreshold: false,
        lineWidth: 0,
        cursor: 'pointer',
        marker: {
          radius: 3,
          symbol: 'circle',
        },
        zIndex: 5,
      },
    },
    accessibility: {
      enabled: false,
    },
  };
}
